import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ReactMarkdown from "react-markdown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeRounded";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

import { useTranslation } from "utils/locales/utilityFunctions";

import { styles } from "../styles";
import { TypingIndicator } from "./typingIndicator";
import { AiChatMessage } from "types/auth";

type MessageBubbleProps = {
    message: AiChatMessage;
    showStartupText?: boolean;
};

const markdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
        <Typography
            variant="body1"
            sx={{
                whiteSpace: "pre-wrap",
                mb: 2,
                "&:last-child": {
                    mb: 0,
                },
            }}
        >
            {children}
        </Typography>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
        <Box component="ul" sx={{ pl: 2 }}>
            {children}
        </Box>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
        <Typography component="li" variant="body1">
            {children}
        </Typography>
    ),
    table: ({ children }: any) => (
        <Box sx={{ overflowX: "auto", my: 1 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                {children}
            </table>
        </Box>
    ),
    th: ({ children }: any) => (
        <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>
            {children}
        </th>
    ),
    td: ({ children }: any) => (
        <td style={{ padding: "6px", borderBottom: "1px solid #eee" }}>
            {children}
        </td>
    ),
};

export function MessageBubble({ message, showStartupText }: MessageBubbleProps) {
    const { translate } = useTranslation();

    const [loadingText, setLoadingText] = useState(translate("ai-chat-feedback-init-agent"));

    useEffect(() => {
        const timeout = setTimeout(() => {
            setLoadingText(translate("ai-chat-feedback-getting-respons"));
        }, 2500);

        return () => clearTimeout(timeout);
    }, []);

    const isUser = message.role === "user";
    const isTyping = message.role === "assistant" && !message.content;
    const isError = message.type === "error";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: isUser ? "flex-end" : "flex-start",
            }}
        >
            {!isUser && (
                <Typography
                    variant="caption"
                    sx={{
                        mb: 0.5,
                        color: "text.secondary",
                        opacity: 0.7,
                    }}
                >
                    {translate("ai-chat-assistant-label")}
                </Typography>
            )}

            <Box
                sx={{
                    display: "flex",
                    flexDirection: isUser ? "column" : "row",
                    alignItems: isUser ? "flex-end" : "flex-start",
                    width: "100%",
                    gap: 1
                }}
            >
                {!isUser && (
                    <Box sx={styles.avatar}>
                        <AutoAwesomeIcon color="primary" sx={{ width: 18, height: 18 }} />
                    </Box>
                )}

                <Box sx={isUser ? styles.userBubble : styles.aiBubble}>
                    {isUser ? (
                        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                            {message.content}
                        </Typography>
                    ) : isTyping ? (
                        <TypingIndicator text={showStartupText ? loadingText : undefined} />
                    ) : (
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                            components={markdownComponents}
                        >
                            {isError ? translate(message.content) : message.content}
                        </ReactMarkdown>
                    )}
                </Box>
            </Box>
        </Box>
    );
}